import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, Lightbulb, RefreshCcw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const CHAT_API_ENDPOINT = '/api/chat/stream';
const MAX_HISTORY_MESSAGES = 8;
const CHATBOT_FIRST_OPEN_KEY = 'chatbot_first_opened';
const SUGGESTION_BATCH_SIZE = 3;

const SUGGESTION_POOL = {
  en: [
    'Who is Restu Agil Yuli Arjun?',
    'What are Restu\'s main skills?',
    'What projects has Restu built?',
    'Tell me about Restu\'s work experience.',
    'How can I contact Restu?',
    'What certifications does Restu have?',
    'Tell me about Restu\'s thesis topic.',
    'What is Restu\'s TOEFL score?',
    'Can you summarize Restu\'s profile in 5 points?',
    'What publications or patents does Restu have?',
  ],
  id: [
    'Siapa Restu Agil Yuli Arjun?',
    'Apa keahlian utama Restu?',
    'Proyek apa saja yang pernah Restu kerjakan?',
    'Ceritakan pengalaman kerja Restu.',
    'Bagaimana cara menghubungi Restu?',
    'Sertifikasi apa saja yang dimiliki Restu?',
    'Apa topik skripsi Restu?',
    'Berapa skor TOEFL Restu?',
    'Ringkas profil Restu dalam 5 poin.',
    'Apakah Restu punya publikasi atau HKI?',
  ],
};

const CHAT_UI_TEXT = {
  en: {
    greeting: "Hi! I'm Restu's AI assistant. Ask me anything about his profile, skills, experience, projects, or contact.",
    thinking: 'Thinking...',
    apiError: 'Sorry, the AI service is temporarily unavailable. Please try again in a moment.',
    suggestionsTitle: 'Suggested questions',
    showSuggestions: 'Suggestions',
    hideSuggestions: 'Hide',
    refreshSuggestions: 'Refresh',
  },
  id: {
    greeting: 'Halo! Saya asisten AI portfolio Restu. Tanyakan profil, keahlian, pengalaman, proyek, atau kontak Restu.',
    thinking: 'Thinking...',
    apiError: 'Maaf, layanan AI sedang tidak tersedia sementara. Coba lagi sebentar ya.',
    suggestionsTitle: 'Rekomendasi pertanyaan',
    showSuggestions: 'Rekomendasi',
    hideSuggestions: 'Sembunyikan',
    refreshSuggestions: 'Acak lagi',
  },
};

function getChatUiText(lang) {
  return CHAT_UI_TEXT[lang] || CHAT_UI_TEXT.en;
}

function shuffleList(items) {
  const clone = [...items];

  for (let i = clone.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [clone[i], clone[j]] = [clone[j], clone[i]];
  }

  return clone;
}

function pickSuggestionBatch(pool, count, previousBatch = []) {
  if (!Array.isArray(pool) || pool.length === 0) {
    return [];
  }

  if (pool.length <= count) {
    return [...pool];
  }

  const previousSet = new Set(previousBatch);
  const nonRepeated = pool.filter((question) => !previousSet.has(question));
  const source = nonRepeated.length >= count ? nonRepeated : pool;

  return shuffleList(source).slice(0, count);
}

function detectInputLanguage(text) {
  const tokens = String(text || '')
    .toLowerCase()
    .match(/[a-z]+/g) || [];

  if (tokens.length === 0) {
    return null;
  }

  const idStrong = new Set([
    'siapa', 'apa', 'bagaimana', 'tolong', 'jelaskan', 'apakah', 'mengapa', 'dimana',
    'saya', 'aku', 'kamu', 'anda', 'keahlian', 'pengalaman', 'proyek', 'kontak',
    'publikasi', 'paten', 'pendidikan', 'sertifikat', 'bahasa',
  ]);
  const enStrong = new Set([
    'who', 'what', 'how', 'why', 'where', 'please', 'explain', 'about', 'your',
    'skills', 'experience', 'projects', 'contact', 'publications', 'publication',
    'journal', 'patent', 'education', 'certification', 'language',
  ]);

  let idHits = 0;
  let enHits = 0;

  for (const token of tokens) {
    if (idStrong.has(token)) idHits += 1;
    if (enStrong.has(token)) enHits += 1;
  }

  if (idHits > enHits && idHits > 0) {
    return 'id';
  }

  if (enHits > idHits && enHits > 0) {
    return 'en';
  }

  return null;
}

function toApiHistory(messages) {
  return messages
    .filter(
      (msg) =>
        (msg.role === 'user' || msg.role === 'bot') &&
        typeof msg.text === 'string' &&
        msg.text.trim().length > 0,
    )
    .slice(-MAX_HISTORY_MESSAGES)
    .map((msg) => ({
      role: msg.role === 'bot' ? 'assistant' : 'user',
      content: msg.text,
    }));
}

function createMessageId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function parseSseEventBlock(block) {
  let eventName = 'message';
  let dataText = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    }

    if (line.startsWith('data:')) {
      dataText += line.slice(5).trim();
    }
  }

  if (!dataText) {
    return null;
  }

  try {
    const payload = JSON.parse(dataText);
    return { eventName, payload };
  } catch {
    return null;
  }
}

async function requestAssistantReplyStream({ message, history, lang, onDelta, onThinking }) {
  const response = await fetch(CHAT_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      history,
      lang,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const reason = data?.error || `Request failed with status ${response.status}`;
    throw new Error(reason);
  }

  if (!response.body) {
    throw new Error('Streaming response body is not available.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;

  const processBuffer = () => {
    let boundary = buffer.indexOf('\n\n');

    while (boundary !== -1) {
      const rawBlock = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 2);

      if (rawBlock) {
        const parsed = parseSseEventBlock(rawBlock);
        if (parsed) {
          const { eventName, payload } = parsed;

          if (eventName === 'thinking') {
            if (typeof onThinking === 'function') {
              onThinking(payload?.text || 'Thinking...');
            }
          } else if (eventName === 'delta') {
            if (typeof onDelta === 'function' && typeof payload?.text === 'string') {
              onDelta(payload.text);
            }
          } else if (eventName === 'done') {
            donePayload = payload;
          } else if (eventName === 'error') {
            throw new Error(payload?.error || 'Streaming failed.');
          }
        }
      }

      boundary = buffer.indexOf('\n\n');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    processBuffer();
  }

  const tail = decoder.decode();
  if (tail) {
    buffer += tail.replace(/\r\n/g, '\n');
  }

  if (buffer.trim()) {
    buffer += '\n\n';
    processBuffer();
  }

  if (!donePayload || typeof donePayload.reply !== 'string') {
    throw new Error('No final response from AI stream.');
  }

  return donePayload;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(() => localStorage.getItem(CHATBOT_FIRST_OPEN_KEY) === '1');
  const [showSuggestions, setShowSuggestions] = useState(() => localStorage.getItem(CHATBOT_FIRST_OPEN_KEY) !== '1');
  const { lang } = useLanguage();
  const uiText = getChatUiText(lang);
  const suggestionPool = SUGGESTION_POOL[lang] || SUGGESTION_POOL.en;
  const [visibleSuggestions, setVisibleSuggestions] = useState(() =>
    pickSuggestionBatch(suggestionPool, SUGGESTION_BATCH_SIZE),
  );
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const nextPool = SUGGESTION_POOL[lang] || SUGGESTION_POOL.en;
    setVisibleSuggestions((prev) => pickSuggestionBatch(nextPool, SUGGESTION_BATCH_SIZE, prev));
  }, [lang]);

  const handleRefreshSuggestions = () => {
    setVisibleSuggestions((prev) => pickSuggestionBatch(suggestionPool, SUGGESTION_BATCH_SIZE, prev));
  };

  const handleToggleChat = () => {
    setOpen((prev) => {
      const nextOpen = !prev;

      if (nextOpen) {
        if (messages.length === 0) {
          setMessages([{ id: createMessageId(), role: 'bot', text: uiText.greeting }]);
        }

        setVisibleSuggestions((prev) => pickSuggestionBatch(suggestionPool, SUGGESTION_BATCH_SIZE, prev));

        if (!hasOpenedOnce) {
          localStorage.setItem(CHATBOT_FIRST_OPEN_KEY, '1');
          setHasOpenedOnce(true);
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      }

      return nextOpen;
    });
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async (messageText) => {
    const trimmed = messageText.trim();
    if (!trimmed || isLoading) return;

    const preferredLang = detectInputLanguage(trimmed) || lang;
    const requestUiText = getChatUiText(preferredLang);
    const apiHistory = toApiHistory(messages);
    const userMsg = { id: createMessageId(), role: 'user', text: trimmed };
    const streamingBotId = createMessageId();

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: streamingBotId,
        role: 'bot',
        text: '',
        isPending: true,
        pendingText: requestUiText.thinking,
      },
    ]);

    setInput('');
    setShowSuggestions(false);
    setIsLoading(true);

    try {
      const { reply, modelUsed } = await requestAssistantReplyStream({
        message: trimmed,
        history: apiHistory,
        lang: preferredLang,
        onThinking: (thinkingText) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingBotId
                ? { ...msg, pendingText: thinkingText || requestUiText.thinking }
                : msg,
            ),
          );
        },
        onDelta: (delta) => {
          if (!delta) return;

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingBotId
                ? { ...msg, text: `${msg.text || ''}${delta}` }
                : msg,
            ),
          );
        },
      });

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingBotId
            ? {
              ...msg,
              text: reply,
              model: modelUsed,
              isPending: false,
              pendingText: '',
            }
            : msg,
        ),
      );
    } catch (error) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingBotId
            ? {
              ...msg,
              text: requestUiText.apiError,
              fallback: true,
              isPending: false,
              pendingText: '',
            }
            : msg,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    await sendMessage(input);
  };

  const handleSuggestionClick = async (question) => {
    if (isLoading) return;
    await sendMessage(question);
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={handleToggleChat}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full bg-linear-to-br from-accent-500 to-cyan-500 text-white shadow-lg shadow-accent-500/30 flex items-center justify-center hover:scale-110 transition-transform"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle chatbot"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-20 right-3 left-3 sm:left-auto sm:bottom-24 sm:right-6 z-50 w-auto sm:w-96 max-h-[70vh] flex flex-col chat-shell rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-surface-border chat-pane">
              <div className="w-9 h-9 rounded-full bg-linear-to-br from-accent-500 to-cyan-500 flex items-center justify-center">
                <Bot size={18} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-heading text-sm font-semibold">Restu's Assistant</p>
                <p className="text-faint text-xs">
                  {lang === 'id' ? 'Selalu online' : 'Always online'}
                </p>
              </div>
              {hasOpenedOnce && (
                <button
                  type="button"
                  onClick={() => setShowSuggestions((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-muted hover:text-heading hover:bg-surface transition-colors"
                >
                  <Lightbulb size={12} />
                  {showSuggestions ? uiText.hideSuggestions : uiText.showSuggestions}
                </button>
              )}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-50 max-h-[50vh] chat-pane">
              {messages.map((msg, i) => (
                <motion.div
                  key={msg.id || i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-accent-500 text-white rounded-br-md'
                        : 'chat-bot-bubble text-body rounded-bl-md'
                    }`}
                  >
                    {msg.isPending && !msg.text ? msg.pendingText || uiText.thinking : msg.text}
                  </div>
                </motion.div>
              ))}
            </div>

            {showSuggestions && (
              <div className="px-4 pb-3 chat-pane">
                <div className="rounded-xl border border-surface-border bg-surface/85 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-[11px] text-faint">{uiText.suggestionsTitle}</p>
                    <button
                      type="button"
                      onClick={handleRefreshSuggestions}
                      disabled={isLoading}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] text-muted hover:text-heading hover:bg-surface transition-colors disabled:opacity-60"
                    >
                      <RefreshCcw size={11} />
                      {uiText.refreshSuggestions}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {visibleSuggestions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => handleSuggestionClick(question)}
                        disabled={isLoading}
                        className="w-full min-w-0 text-left px-3 py-2 rounded-lg text-xs leading-snug whitespace-normal wrap-break-word text-body bg-surface border border-surface-border hover:border-accent-500/40 hover:text-heading transition-colors disabled:opacity-60"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="px-4 py-3 border-t border-surface-border chat-pane">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={isLoading}
                  placeholder={lang === 'id' ? 'Ketik pesan...' : 'Type a message...'}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm text-heading placeholder:text-faint bg-surface border border-surface-border outline-none focus:border-accent-500/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="w-10 h-10 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white flex items-center justify-center disabled:opacity-40 hover:shadow-lg hover:shadow-accent-500/20 transition-all"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
